import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  BedDouble, 
  MessageSquare, 
  Calendar, 
  Star, 
  Tag, 
  Mail,
  TrendingUp,
  Users,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token, admin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = [
    { label: 'Total Rooms', value: stats?.rooms || 0, icon: BedDouble, color: 'bg-emerald-500' },
    { label: 'Blog Posts', value: stats?.blog_posts || 0, icon: FileText, color: 'bg-violet-500' },
    { label: 'Inquiries', value: stats?.inquiries || 0, icon: MessageSquare, color: 'bg-blue-500' },
    { label: 'Pending Inquiries', value: stats?.pending_inquiries || 0, icon: TrendingUp, color: 'bg-amber-500' },
    { label: 'Event Inquiries', value: stats?.event_inquiries || 0, icon: Calendar, color: 'bg-purple-500' },
    { label: 'Testimonials', value: stats?.testimonials || 0, icon: Star, color: 'bg-yellow-500' },
    { label: 'Active Offers', value: stats?.offers || 0, icon: Tag, color: 'bg-red-500' },
    { label: 'Newsletter Subs', value: stats?.newsletter_subscribers || 0, icon: Mail, color: 'bg-indigo-500' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-emerald-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-foreground">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Welcome back, {admin?.username}!</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card className="hover:shadow-lg transition-shadow" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.label}
                </CardTitle>
                <div className={`p-2 rounded-lg ${stat.color}`}>
                  <stat.icon className="w-4 h-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-foreground">{stat.value}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <a href="/admin/rooms" className="block p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <span className="font-medium">Manage Rooms</span>
                <p className="text-sm text-muted-foreground">Add, edit, or remove room listings</p>
              </a>
              <a href="/admin/blog" className="block p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <span className="font-medium">Write Blog Post</span>
                <p className="text-sm text-muted-foreground">Create travel guides and articles</p>
              </a>
              <a href="/admin/inquiries" className="block p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <span className="font-medium">View Inquiries</span>
                <p className="text-sm text-muted-foreground">Respond to customer messages</p>
              </a>
              <a href="/admin/offers" className="block p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors">
                <span className="font-medium">Manage Offers</span>
                <p className="text-sm text-muted-foreground">Create and edit special promotions</p>
              </a>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-600" />
              System Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Logged in as</span>
                <span className="font-medium">{admin?.username}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{admin?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">API Status</span>
                <span className="font-medium text-emerald-600">Connected</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
